import { useQuery } from 'react-query';
import { fetcher } from '../utils/api';
import { ApiRoutes } from '../constants/routes';

// Hook to fetch user credit balance
export const useUserCreditBalance = (options = {}) => {
  // Check if upload is in progress to disable polling
  const isUploading = typeof window !== 'undefined' && localStorage.getItem('isUploading') === 'true';
  // Check if music is playing to reduce polling frequency
  const isPlaying = typeof window !== 'undefined' && 
    (document.querySelector('audio')?.paused === false || 
     localStorage.getItem('musicPlayer_isPlaying') === 'true');
  
  return useQuery(
    'userCreditBalance',
    async () => {
      if (!ApiRoutes.User) {
        throw new Error('API route not available');
      }
      const response = await fetcher(ApiRoutes.User);
      return response.credit_balance || 0;
    },
    {
      // Reduce polling frequency: every 60 seconds normally, every 2 minutes during playback, disabled during upload
      refetchInterval: isUploading ? false : (isPlaying ? 120000 : 60000),
      staleTime: isPlaying ? 60000 : 30000, // Consider data stale after longer time during playback
      enabled: !!ApiRoutes.User && !isUploading, // Only run if API route is available and not uploading
      ...options
    }
  );
};

// Hook to fetch user earnings
export const useUserEarnings = (startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);

  return useQuery(
    ['userEarnings', startDate, endDate],
    async () => {
      const url = params.toString() ? `${ApiRoutes.UserEarnings}?${params.toString()}` : ApiRoutes.UserEarnings;
      const response = await fetcher(url);
      return response;
    },
    {
      refetchInterval: 60000, // Refetch every minute
      staleTime: 30000, // Consider data stale after 30 seconds
    }
  );
};

// Hook to fetch user payout history
export const useUserPayouts = () => {
  return useQuery(
    'userPayouts',
    async () => {
      const response = await fetcher(ApiRoutes.UserPayouts);
      return response.payouts || [];
    },
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      staleTime: 120000, // Consider data stale after 2 minutes
    }
  );
};

// Hook to fetch user payment methods
export const useUserPaymentMethods = () => {
  return useQuery(
    'userPaymentMethods',
    async () => {
      if (!ApiRoutes.PaymentMethods) {
        throw new Error('API route not available');
      }
      const response = await fetcher(ApiRoutes.PaymentMethods);
      return response.payment_methods || [];
    },
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      staleTime: 120000, // Consider data stale after 2 minutes
      enabled: !!ApiRoutes.PaymentMethods, // Only run if API route is available
    }
  );
};

// Hook to fetch user deposit transactions
export const useUserDepositTransactions = () => {
  return useQuery(
    'userDepositTransactions',
    async () => {
      const response = await fetcher(ApiRoutes.DepositTransactions);
      return response.transactions || [];
    },
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      staleTime: 120000, // Consider data stale after 2 minutes
    }
  );
};

// Hook to fetch user auto-deposit settings
export const useUserAutoDepositSettings = (options = {}) => {
  return useQuery(
    'userAutoDepositSettings',
    async () => {
      if (!ApiRoutes.AutoDepositSettings) {
        throw new Error('API route not available');
      }
      const response = await fetcher(ApiRoutes.AutoDepositSettings);
      return response;
    },
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      staleTime: 120000, // Consider data stale after 2 minutes
      enabled: !!ApiRoutes.AutoDepositSettings, // Only run if API route is available
      ...options
    }
  );
};

// Hook to fetch creator acts
export const useCreatorActs = () => {
  return useQuery(
    'creatorActs',
    async () => {
      const response = await fetcher(ApiRoutes.CreatorActs);
      return response || [];
    },
    {
      refetchInterval: 300000, // Refetch every 5 minutes
      staleTime: 120000, // Consider data stale after 2 minutes
    }
  );
};
